import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import DreamiePromptModal, {
  type DreamiePromptTab,
} from './DreamiePromptModal';
import { generateMessageColors } from './sidebar/threadUtils';
import { FileUploadState } from './sidebar/DirectFileUploader';
import { useFileUpload } from './sidebar/useFileUpload';
import api from '../../api';

interface QuickAnswerResult {
  results:
    | {
        type: 'answer';
        answer: string;
        sourcesUsed: string[];
      }
    | {
        type: 'escalation';
        message: string;
        options: Array<{
          label: string;
          action: 'create_todo' | 'create_project_todo';
          suggestedTitle: string;
          suggestedDescription: string;
          projectBriefing?: string | null;
        }>;
      };
  summary: string;
}

type ModalPhase = 'prompt' | 'thinking' | 'streaming' | 'response';

interface MessageAttachment {
  url: string;
  name: string;
  type: string;
  previewUrl?: string;
}

interface ConversationMessage {
  question: string;
  answer?: string;
  escalation?: QuickAnswerResult['results'] & { type: 'escalation' };
  error?: string;
  attachments?: MessageAttachment[];
  createdAt?: string;
}

interface PersistedMessage {
  id: string;
  question: string;
  answer: string | null;
  escalation: any;
  attachments: any;
  createdAt: string | null;
}

interface PersistedThread {
  threadId: string;
  lastMessageAt: string | null;
  messages: PersistedMessage[];
}

interface QuickAskModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId?: string;
  agentImageUrl?: string;
  agentNickname?: string;
  predominantColour?: string;
  onCreateTodo?: (title: string, description: string) => void;
  onCreateProject?: (
    title: string,
    description: string,
    projectBriefing?: string | null,
  ) => void;
}

const formatToolActivity = (
  tool: { name: string; args?: Record<string, any> },
  nickname: string,
): string => {
  const args = tool.args || {};
  switch (tool.name) {
    case 'readWebPage':
      return args.url
        ? `${nickname} is visiting ${args.url}`
        : `${nickname} is reading a web page`;
    case 'searchEmails':
      return args.query
        ? `${nickname} is searching emails for "${args.query}"`
        : `${nickname} is searching emails`;
    case 'getEmailDetails':
      return `${nickname} is reading an email`;
    case 'readCalendar':
      return `${nickname} is checking the calendar`;
    case 'readSubThreadHistory':
      return `${nickname} is reviewing a task`;
    case 'rememberFact':
      return `${nickname} is remembering`;
    default:
      return `${nickname} is looking something up`;
  }
};

const isRememberTool = (tool: { name: string } | null): boolean =>
  tool?.name === 'rememberFact';

/**
 * Format a context-gathering status event into a user-facing label.
 * Fired by the QAS backend before the model starts streaming so the user
 * sees what the agent is fetching instead of a generic "thinking...".
 */
const formatContextStatus = (
  status: { type: string; terms?: string[] },
  nickname: string,
): string | null => {
  if (status.type === 'searchTerms' && Array.isArray(status.terms)) {
    const terms = status.terms.filter(Boolean).slice(0, 2);
    if (terms.length === 0) return null;
    const list =
      status.terms.length > 2
        ? `${terms.join(', ')} and more`
        : terms.join(' and ');
    return `${nickname} is building context on ${list}...`;
  }
  return null;
};

const SUGGESTIONS = [
  {
    label: 'Need an update on a task?',
    value: 'Can you give me an update on my current tasks?',
  },
  {
    label: "What's on my calendar today?",
    value: "What's on my calendar today?",
  },
  {
    label: 'Teach me a preference or fact',
    value: 'I want you to remember that ',
  },
];

const QuickAskModal: React.FC<QuickAskModalProps> = ({
  isOpen,
  onClose,
  agentId,
  agentImageUrl,
  agentNickname,
  predominantColour,
  onCreateTodo,
  onCreateProject,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [inputValue, setInputValue] = useState('');
  const [phase, setPhase] = useState<ModalPhase>('prompt');
  const [activeTab, setActiveTab] = useState<DreamiePromptTab>('chat');
  const [askedQuestion, setAskedQuestion] = useState('');
  const [result, setResult] = useState<QuickAnswerResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [streamedText, setStreamedText] = useState('');
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [historyThreads, setHistoryThreads] = useState<PersistedThread[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeTool, setActiveTool] = useState<{
    name: string;
    args?: Record<string, any>;
  } | null>(null);
  const [rememberedMessage, setRememberedMessage] = useState<string | null>(
    null,
  );
  const [contextActivity, setContextActivity] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const responseAreaRef = useRef<HTMLDivElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadState[]>([]);
  const [currentAttachments, setCurrentAttachments] = useState<
    MessageAttachment[]
  >([]);

  const handleFileUpload = useCallback((fileState: FileUploadState) => {
    setUploadedFiles((prev) => {
      const existing = prev.findIndex((f) => f.id === fileState.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = fileState;
        return updated;
      }
      return [...prev, fileState];
    });
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const { processFile, cleanupPreviews } = useFileUpload(handleFileUpload);

  const handleAttachFiles = useCallback(
    (files: File[]) => {
      files.forEach((file) => processFile(file));
    },
    [processFile],
  );

  // ── Fetch persisted history when modal opens ──────────────────
  useEffect(() => {
    if (!isOpen || !agentId) return;
    let cancelled = false;

    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get<{
          success: boolean;
          threads: PersistedThread[];
        }>('/agents/quick-ask-history', {
          params: { agent_id: agentId },
        });
        if (!cancelled && response.data.success) {
          setHistoryThreads(response.data.threads);
        }
      } catch (err) {
        console.error('Error fetching quick-ask history:', err);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [isOpen, agentId, getAccessTokenSilently]);

  // ── Character drip buffer for smooth streaming ─────────────────
  const rawBufferRef = useRef('');
  const displayedLenRef = useRef(0);
  const dripTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const CHARS_PER_TICK = 3;
  const TICK_MS = 16;

  const startDrip = useCallback(() => {
    if (dripTimerRef.current) return;
    displayedLenRef.current = 0;
    dripTimerRef.current = setInterval(() => {
      const buf = rawBufferRef.current;
      const cur = displayedLenRef.current;
      if (cur < buf.length) {
        const charsToAdd = Math.min(CHARS_PER_TICK, buf.length - cur);
        displayedLenRef.current = cur + charsToAdd;
        setStreamedText(buf.slice(0, displayedLenRef.current));
      }
    }, TICK_MS);
  }, []);

  const stopDrip = useCallback(() => {
    if (dripTimerRef.current) {
      clearInterval(dripTimerRef.current);
      dripTimerRef.current = null;
    }
    // Flush any remaining content
    if (
      rawBufferRef.current &&
      displayedLenRef.current < rawBufferRef.current.length
    ) {
      setStreamedText(rawBufferRef.current);
      displayedLenRef.current = rawBufferRef.current.length;
    }
  }, []);

  const appendToken = useCallback((text: string) => {
    rawBufferRef.current += text;
  }, []);

  // Cleanup drip timer on unmount
  useEffect(() => {
    return () => stopDrip();
  }, [stopDrip]);

  // Auto-scroll response area to bottom
  useEffect(() => {
    const el = responseAreaRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [phase, streamedText, messages]);

  const reset = useCallback(() => {
    setInputValue('');
    setPhase('prompt');
    setAskedQuestion('');
    setResult(null);
    setError(null);
    setStreamedText('');
    setActiveTool(null);
    setContextActivity(null);
    setRememberedMessage(null);
    setMessages([]);
    setCurrentThreadId(null);
    setUploadedFiles([]);
    setCurrentAttachments([]);
    setActiveTab('chat');
    cleanupPreviews();
    rawBufferRef.current = '';
    displayedLenRef.current = 0;
    stopDrip();
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, [cleanupPreviews]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSubmit = useCallback(
    async (question: string) => {
      // Build the full history locally (including the current exchange)
      // before setMessages updates — avoids stale-closure issues
      const currentExchange: ConversationMessage | null = askedQuestion
        ? {
            question: askedQuestion,
            answer: streamedText || undefined,
            escalation:
              result?.results.type === 'escalation'
                ? (result.results as ConversationMessage['escalation'])
                : undefined,
            error: error || undefined,
            attachments:
              currentAttachments.length > 0 ? currentAttachments : undefined,
          }
        : null;
      const fullHistory = currentExchange
        ? [...messages, currentExchange]
        : messages;

      // Save to state for rendering
      if (currentExchange) {
        setMessages(fullHistory);
      }

      // Collect successfully uploaded file URLs and build attachments
      const imageUrls: string[] = [];
      const fileUrls: string[] = [];
      const attachments: MessageAttachment[] = [];
      for (const f of uploadedFiles) {
        if (f.status === 'success' && f.url) {
          attachments.push({
            url: f.url,
            name: f.name,
            type: f.type,
            previewUrl: f.previewUrl,
          });
          if (f.type.startsWith('image/')) {
            imageUrls.push(f.url);
          } else {
            fileUrls.push(f.url);
          }
        }
      }
      setCurrentAttachments(attachments);
      setUploadedFiles([]);
      setAskedQuestion(question);
      setInputValue('');
      setPhase('thinking');
      setError(null);
      setResult(null);
      setStreamedText('');
      setActiveTool(null);
      setContextActivity(null);
      setRememberedMessage(null);
      rawBufferRef.current = '';
      displayedLenRef.current = 0;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/agents/quick-ask`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              Accept: 'text/event-stream',
            },
            body: JSON.stringify({
              question,
              agent_id: agentId,
              ...(currentThreadId ? { thread_id: currentThreadId } : {}),
              history: fullHistory.map((m) => ({
                role: 'user' as const,
                content: m.question,
                ...(m.answer ? { answer: m.answer } : {}),
                ...(m.escalation ? { escalation: m.escalation.message } : {}),
              })),
              ...(imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
              ...(fileUrls.length > 0 ? { file_urls: fileUrls } : {}),
            }),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          const errBody = await response.json().catch(() => null);
          throw new Error(errBody?.error || `Server error ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';
        let streamDone = false;

        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) {
            streamDone = true;
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
              try {
                const data = JSON.parse(line.slice(6));
                switch (currentEvent) {
                  case 'thread_id':
                    setCurrentThreadId(data.thread_id);
                    break;
                  case 'token':
                    setPhase((p) => {
                      if (p === 'thinking') {
                        setActiveTool(null);
                        setContextActivity(null);
                        startDrip();
                        return 'streaming';
                      }
                      return p;
                    });
                    appendToken(data.text);
                    break;
                  case 'status': {
                    const label = formatContextStatus(data, nickname);
                    if (label) setContextActivity(label);
                    break;
                  }
                  case 'tool_start':
                    setActiveTool({ name: data.tool, args: data.args });
                    break;
                  case 'tool_end':
                    if (data.tool === 'rememberFact') {
                      setActiveTool(null);
                      setRememberedMessage(
                        `${agentNickname || 'your assistant'} added a memory`,
                      );
                    } else {
                      // During streaming, clear tool label immediately.
                      // During thinking, keep showing the last tool so user
                      // sees activity — it'll be replaced by the next tool or
                      // cleared when streaming starts.
                      setPhase((p) => {
                        if (p !== 'thinking') setActiveTool(null);
                        return p;
                      });
                    }
                    break;
                  case 'escalation':
                    setResult({
                      results: { type: 'escalation', ...data },
                      summary: '',
                    });
                    setPhase('response');
                    break;
                  case 'done':
                    setPhase('response');
                    break;
                  case 'error':
                    setError(data.message);
                    setPhase('response');
                    break;
                }
              } catch {
                // ignore malformed JSON
              }
              currentEvent = '';
            }
          }
        }

        // Stop drip and flush remaining content
        stopDrip();
        // Ensure we land on response phase when stream ends
        setPhase((prev) => (prev === 'streaming' ? 'response' : prev));
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Quick ask error:', err);
        setError(err instanceof Error ? err.message : 'Something went wrong');
        setPhase('response');
      } finally {
        abortRef.current = null;
      }
    },
    [
      getAccessTokenSilently,
      agentId,
      currentThreadId,
      startDrip,
      stopDrip,
      appendToken,
      askedQuestion,
      streamedText,
      result,
      error,
      uploadedFiles,
      currentAttachments,
      messages,
    ],
  );

  const handleEscalation = useCallback(
    (
      action: 'create_todo' | 'create_project_todo',
      title: string,
      description: string,
      projectBriefing?: string | null,
    ) => {
      if (action === 'create_todo' && onCreateTodo) {
        onCreateTodo(title, description);
      } else if (action === 'create_project_todo' && onCreateProject) {
        onCreateProject(title, description, projectBriefing);
      }
      handleClose();
    },
    [onCreateTodo, onCreateProject, handleClose],
  );

  const nickname = agentNickname || 'your assistant';

  const agentMessageColors = useMemo(
    () => (predominantColour ? generateMessageColors(predominantColour) : null),
    [predominantColour],
  );

  const formatTimestamp = useCallback((iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    if (isToday) return time;
    if (isYesterday) return `Yesterday ${time}`;
    return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${time}`;
  }, []);

  const historyContent = useMemo(() => {
    if (historyLoading) {
      return (
        <div className='dreamie-prompt-history-placeholder'>
          <p>Loading history...</p>
        </div>
      );
    }
    if (historyThreads.length === 0) return null;

    return (
      <div className='dreamie-prompt-history-list'>
        {historyThreads.map((thread) => {
          if (!thread.messages || thread.messages.length === 0) return null;
          const firstMsg = thread.messages[0];
          const lastMsg = thread.messages[thread.messages.length - 1];
          const preview = lastMsg?.answer
            ? lastMsg.answer.length > 120
              ? lastMsg.answer.slice(0, 120) + '...'
              : lastMsg.answer
            : lastMsg?.escalation?.message
              ? lastMsg.escalation.message.length > 120
                ? lastMsg.escalation.message.slice(0, 120) + '...'
                : lastMsg.escalation.message
              : null;

          return (
            <div key={thread.threadId} className='dreamie-prompt-history-item'>
              <div className='dreamie-prompt-history-question'>
                <span className='dreamie-prompt-history-text'>
                  {firstMsg?.question || 'Untitled conversation'}
                </span>
                <span className='dreamie-prompt-history-time'>
                  {formatTimestamp(thread.lastMessageAt)}
                </span>
              </div>
              {thread.messages.length > 1 && (
                <span className='dreamie-prompt-history-count'>
                  {thread.messages.length} messages
                </span>
              )}
              {preview && (
                <div
                  className='dreamie-prompt-history-answer'
                  style={agentMessageColors || undefined}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                    {preview}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [historyThreads, historyLoading, agentMessageColors, formatTimestamp]);

  return (
    <DreamiePromptModal
      isOpen={isOpen}
      onClose={handleClose}
      avatarUrl={phase === 'prompt' ? agentImageUrl : undefined}
      avatarAlt={nickname}
      heading={phase === 'prompt' ? `Chat with ${nickname}` : ''}
      suggestions={phase === 'prompt' ? SUGGESTIONS : undefined}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSubmit={handleSubmit}
      submitting={phase === 'thinking' || phase === 'streaming'}
      placeholder={`Ask or teach ${nickname} anything...`}
      inputPosition={phase === 'prompt' ? 'above' : 'below'}
      uploadedFiles={uploadedFiles}
      onFileUpload={handleFileUpload}
      onRemoveFile={handleRemoveFile}
      onAttachFiles={handleAttachFiles}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      showBreadcrumbs={phase !== 'prompt'}
      onBackToChats={reset}
      historyContent={null}
    >
      {/* Response area — shown after submit */}
      {phase !== 'prompt' && (
        <>
          <div className='dreamie-prompt-response-area' ref={responseAreaRef}>
            {/* Previous conversation messages */}
            {messages.map((msg, i) => (
              <React.Fragment key={i}>
                {msg.attachments && msg.attachments.length > 0 && (
                  <div className='dreamie-prompt-attachments'>
                    {msg.attachments.map((a, j) =>
                      a.type.startsWith('image/') ? (
                        <img
                          key={j}
                          src={a.previewUrl || a.url}
                          alt={a.name}
                          className='dreamie-prompt-attachment-thumb'
                        />
                      ) : (
                        <div key={j} className='dreamie-prompt-attachment-file'>
                          {a.name}
                        </div>
                      ),
                    )}
                  </div>
                )}
                <div className='dreamie-prompt-question-bubble'>
                  {msg.question}
                </div>
                {msg.answer && (
                  <div
                    className='dreamie-prompt-answer'
                    style={agentMessageColors || undefined}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {msg.answer}
                    </ReactMarkdown>
                  </div>
                )}
                {msg.escalation && (
                  <div
                    className='dreamie-prompt-answer'
                    style={agentMessageColors || undefined}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {msg.escalation.message}
                    </ReactMarkdown>
                  </div>
                )}
                {msg.error && (
                  <div className='dreamie-prompt-answer'>
                    <p>Sorry, something went wrong.</p>
                  </div>
                )}
              </React.Fragment>
            ))}

            {/* Current question attachments + bubble */}
            {currentAttachments.length > 0 && (
              <div className='dreamie-prompt-attachments'>
                {currentAttachments.map((a, j) =>
                  a.type.startsWith('image/') ? (
                    <img
                      key={j}
                      src={a.previewUrl || a.url}
                      alt={a.name}
                      className='dreamie-prompt-attachment-thumb'
                    />
                  ) : (
                    <div key={j} className='dreamie-prompt-attachment-file'>
                      {a.name}
                    </div>
                  ),
                )}
              </div>
            )}
            <div className='dreamie-prompt-question-bubble'>
              {askedQuestion}
            </div>

            {/* Thinking state — also shown during early streaming before text appears */}
            {(phase === 'thinking' ||
              (phase === 'streaming' && !streamedText.trim())) && (
              <div className='dreamie-prompt-thinking'>
                {agentImageUrl && (
                  <div className='thinking-avatar'>
                    <img src={agentImageUrl} alt={nickname} />
                  </div>
                )}
                <span
                  className={`thinking-label${isRememberTool(activeTool) || rememberedMessage ? ' shimmer' : ''}`}
                >
                  {rememberedMessage
                    ? rememberedMessage
                    : activeTool
                      ? formatToolActivity(activeTool, nickname)
                      : contextActivity
                        ? contextActivity
                        : `${nickname} is thinking...`}
                </span>
              </div>
            )}

            {/* Streamed answer (drip-rendered with markdown) */}
            {streamedText.trim() &&
              (phase === 'streaming' || (phase === 'response' && !result)) && (
                <div
                  className='dreamie-prompt-answer'
                  style={agentMessageColors || undefined}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                    {streamedText}
                  </ReactMarkdown>
                  {phase === 'streaming' &&
                    (activeTool || rememberedMessage) && (
                      <div
                        className={`dreamie-prompt-tool-activity${isRememberTool(activeTool) || rememberedMessage ? ' shimmer' : ''}`}
                      >
                        {rememberedMessage
                          ? rememberedMessage
                          : activeTool
                            ? formatToolActivity(activeTool, nickname)
                            : ''}
                      </div>
                    )}
                </div>
              )}

            {/* Error state */}
            {phase === 'response' && error && (
              <div className='dreamie-prompt-answer'>
                <p>Sorry, something went wrong. Please try again.</p>
              </div>
            )}

            {/* Escalation response */}
            {phase === 'response' && result?.results.type === 'escalation' && (
              <div
                className='dreamie-prompt-answer'
                style={agentMessageColors || undefined}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                  {result.results.message}
                </ReactMarkdown>
              </div>
            )}
          </div>

          {/* Escalation buttons — outside scroller */}
          {phase === 'response' && result?.results.type === 'escalation' && (
            <div className='dreamie-prompt-escalation'>
              {result.results.options.map((option, i) => (
                <button
                  key={i}
                  className='dreamie-prompt-escalation-btn'
                  onClick={() =>
                    handleEscalation(
                      option.action,
                      option.suggestedTitle,
                      option.suggestedDescription,
                      option.projectBriefing,
                    )
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </DreamiePromptModal>
  );
};

export default QuickAskModal;
